import { useEffect, useState } from 'react'
import { useRequest } from 'app/hooks/useRequest'
import { notifyError } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/**
 * make a request via window.fetch and return the response as a hook return property
 *
 * @param url will make a GET request to this url on mount
 * @returns [response, error]
 */
export function useImmediateRequest<T>(
  url: string,
  showError = true,
): [T | undefined, any | undefined, () => Promise<void>] {
  const [response, setResponse] = useState<T>()
  const [responseError, setResponseError] = useState<any>()

  const request = useRequest(showError)

  const triggerRevalidation = async () => {
    await getResponse()
  }

  const getResponse = async () => {
    setResponse(undefined)
    if (url) {
      try {
        let response = await request(url)
        // useRequest doesn't throw on 400+ responses — it returns { error: "..." } from doorman.
        // Detect this and throw so the catch block sets responseError for callers to handle.
        if (response && typeof response === `object` && `error` in response) {
          throw new Error(String((response as Record<string, unknown>).error))
        }
        setResponse(response as T)
      } catch (err) {
        logError(`Error response from server`, { url, err })
        if (showError) {
          notifyError(`There was a problem loading data from the server. You may need to reload the page.`)
        }
        setResponseError(err)
      }
    }
  }

  useEffect(() => {
    bg(async () => getResponse())
  }, [url])

  return [response, responseError, triggerRevalidation]
}
